import { defineExperiment } from '@learnforge/demo-render/harness';
import type { ExperimentSpec, PanelRow, ActionBarSpec } from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  MarkSpec,
  MarksHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import {
  add,
  dist,
  normalize,
  scale,
  sub,
  inscribedAngle,
  tangentDirection,
  powerOfPoint,
  cyclicOppositeSum,
} from '@learnforge/demo-math/geometry';
import { CIRCLE_PRESETS } from '../data/circle-presets';
import type { CircleMode } from '../data/circle-presets';

// ============================================================================
// Stage constants
// ============================================================================

const CIRCLE_CENTRE: Vec2 = { x: 400, y: 230 };
const CIRCLE_RADIUS = 150;
const SNAP_TOLERANCE_DEG = 10;

// ============================================================================
// Pure Helper Types
// ============================================================================

export interface InscribedModel {
  A: Vec2;
  B: Vec2;
  P: Vec2;
  O: Vec2;
  centralAngleDeg: number;
  inscribedAngleDeg: number;
  isThales: boolean;
  readoutRows: ReadoutRow[];
}

export interface TangentModel {
  T: Vec2;
  O: Vec2;
  tangentDir: Vec2;
  isPerpendicular: boolean;
  tangentEndA: Vec2;
  tangentEndB: Vec2;
}

export interface PowerModel {
  P: Vec2;
  O: Vec2;
  r: number;
  isOutside: boolean;
  A: Vec2;
  B: Vec2;
  C: Vec2;
  D: Vec2;
  productAB: number;
  productCD: number;
  readoutRows: ReadoutRow[];
}

export interface CyclicQuadModel {
  pts: [Vec2, Vec2, Vec2, Vec2];
  sumAlphaGamma: number;
  sumBetaDelta: number;
  readoutRows: ReadoutRow[];
}

export interface SnapToDiameterResult {
  A: Vec2;
  B: Vec2;
  snapped: boolean;
}

// ============================================================================
// Pure Helpers (exported and tested)
// ============================================================================

/**
 * Project a point onto a circle of centre O and radius r.
 */
export function projectOntoCircle(p: Vec2, O: Vec2, r: number): Vec2 {
  const d = sub(p, O);
  const len = Math.sqrt(d.x * d.x + d.y * d.y);
  if (len < 1e-9) {
    // Point is at centre — project to angle 0
    return { x: O.x + r, y: O.y };
  }
  return { x: O.x + (d.x / len) * r, y: O.y + (d.y / len) * r };
}

/**
 * Build the inscribed angle model.
 * Computes inscribed angle ∠APB and central angle ∠AOB.
 * isThales is true when A and B are exactly antipodal.
 */
export function buildInscribedModel(A: Vec2, B: Vec2, P: Vec2, O: Vec2, r: number): InscribedModel {
  const inscribedDeg = inscribedAngle(A, B, P);
  // Inscribed-angle theorem: the central angle ∠AOB subtending the SAME arc as the
  // inscribed angle (the arc NOT containing P) is exactly 2·∠APB. When ∠APB > 90°
  // (P on the minor arc) this is the REFLEX angle at O (> 180°), which the plain
  // unsigned `centralAngle(A, B, O)` (always ≤ 180°) cannot represent — so derive
  // it from the theorem directly to keep "∠AOB = 2·∠APB" true for all positions.
  const centralDeg = 2 * inscribedDeg;
  // Thales: A and B are antipodal when dist(A, B) ≈ 2r
  const diameter = dist(A, B);
  const isThales = Math.abs(diameter - 2 * r) < 1e-3;

  const readoutRows: ReadoutRow[] = [
    {
      id: 'central-vs-inscribed',
      lhs: '∠AOB = 2·∠APB',
      relation: '=',
      rhs: `${centralDeg.toFixed(1)}°`,
      emphasis: 'invariant',
    },
    {
      id: 'inscribed-val',
      lhs: '∠APB',
      relation: '=',
      rhs: `${inscribedDeg.toFixed(1)}°`,
      emphasis: 'invariant',
    },
  ];

  if (isThales) {
    readoutRows.push({
      id: 'thales-badge',
      lhs: 'Thales',
      relation: '=',
      rhs: '∠APB = 90°',
      emphasis: 'invariant',
    });
  }

  return {
    A,
    B,
    P,
    O,
    centralAngleDeg: centralDeg,
    inscribedAngleDeg: inscribedDeg,
    isThales,
    readoutRows,
  };
}

/**
 * Snap A and B to an exact diameter when they are within tolerance degrees of being antipodal.
 * Returns the (possibly snapped) A and B and a flag.
 */
export function snapToDiameter(
  A: Vec2,
  B: Vec2,
  O: Vec2,
  r: number,
  toleranceDeg: number,
): SnapToDiameterResult {
  // Compute angular separation between A and B as seen from O
  const angleA = Math.atan2(A.y - O.y, A.x - O.x) * (180 / Math.PI);
  const angleB = Math.atan2(B.y - O.y, B.x - O.x) * (180 / Math.PI);
  let diff = Math.abs(((angleA - angleB + 360) % 360) - 180);
  if (diff > 180) diff = 360 - diff;

  if (diff <= toleranceDeg) {
    // Snap B to exact antipode of A
    const dirA = normalize(sub(A, O));
    const snappedB: Vec2 = { x: O.x - dirA.x * r, y: O.y - dirA.y * r };
    return { A, B: snappedB, snapped: true };
  }
  return { A, B, snapped: false };
}

/**
 * Build the tangent model for a point T on the circle.
 */
export function buildTangentModel(T: Vec2, O: Vec2): TangentModel {
  const tangentDir = tangentDirection(O, T);
  const extend = 100;
  const tangentEndA = add(T, scale(tangentDir, extend));
  const tangentEndB = add(T, scale(tangentDir, -extend));

  return {
    T,
    O,
    tangentDir,
    isPerpendicular: true,
    tangentEndA,
    tangentEndB,
  };
}

/**
 * Helper: intersect a line through P with direction dir and a circle (O, r).
 * Returns the two t-parameter values (P + t*dir hits the circle), or null if no intersection.
 */
function lineCircleIntersect(P: Vec2, dir: Vec2, O: Vec2, r: number): [number, number] | null {
  const pO = sub(P, O);
  const b = 2 * (pO.x * dir.x + pO.y * dir.y);
  const c = pO.x * pO.x + pO.y * pO.y - r * r;
  const disc = b * b - 4 * c;
  if (disc < 0) return null;
  const sqrtDisc = Math.sqrt(disc);
  return [(-b - sqrtDisc) / 2, (-b + sqrtDisc) / 2];
}

/**
 * Build the power-of-point model.
 * P can be interior (two chords) or exterior (two secants).
 * For interior P: the two lines are along OP and perpendicular to it.
 * For exterior P: the two secant lines are chosen to reliably cross the circle.
 */
export function buildPowerModel(P: Vec2, O: Vec2, r: number): PowerModel {
  const dToO = dist(P, O);
  const isOutside = dToO > r + 1e-6;

  // Primary direction: P → O (guaranteed to cross the circle)
  const rawDir = sub(O, P);
  const rawLen = Math.sqrt(rawDir.x * rawDir.x + rawDir.y * rawDir.y);
  const dir1: Vec2 =
    rawLen < 1e-9 ? { x: 1, y: 0 } : { x: rawDir.x / rawLen, y: rawDir.y / rawLen };

  // Second direction: rotated 40° from dir1 (always intersects if half-angle > 40°)
  // For an exterior point, the half-angle to tangent = arcsin(r/d).
  // When d = r + small epsilon (barely outside), half-angle ≈ 90°, so 40° is fine.
  // When d is very large, half-angle decreases. We clamp the rotation to half the tangent angle.
  let rotAngle = (40 * Math.PI) / 180;
  if (isOutside) {
    const halfAngleTangent = Math.asin(Math.min(1, r / dToO));
    rotAngle = halfAngleTangent * 0.6; // 60% of max angle → safely intersects
  }
  const cosA = Math.cos(rotAngle);
  const sinA = Math.sin(rotAngle);
  const dir2: Vec2 = {
    x: dir1.x * cosA - dir1.y * sinA,
    y: dir1.x * sinA + dir1.y * cosA,
  };

  // Intersect chord 1 with circle
  const ts1 = lineCircleIntersect(P, dir1, O, r);
  let A: Vec2, B: Vec2;
  if (ts1) {
    A = { x: P.x + ts1[0] * dir1.x, y: P.y + ts1[0] * dir1.y };
    B = { x: P.x + ts1[1] * dir1.x, y: P.y + ts1[1] * dir1.y };
  } else {
    A = projectOntoCircle(P, O, r);
    B = A;
  }

  // Intersect chord 2 with circle
  const ts2 = lineCircleIntersect(P, dir2, O, r);
  let C: Vec2, D: Vec2;
  if (ts2) {
    C = { x: P.x + ts2[0] * dir2.x, y: P.y + ts2[0] * dir2.y };
    D = { x: P.x + ts2[1] * dir2.x, y: P.y + ts2[1] * dir2.y };
  } else {
    C = projectOntoCircle(P, O, r);
    D = C;
  }

  // Products: |PA·PB| and |PC·PD|
  const productAB = ts1 ? Math.abs(ts1[0] * ts1[1]) : 0;
  const productCD = ts2 ? Math.abs(ts2[0] * ts2[1]) : 0;

  const pow = Math.abs(powerOfPoint(P, O, r));

  const readoutRows: ReadoutRow[] = [
    {
      id: 'power-product',
      lhs: 'PA·PB = PC·PD',
      relation: '=',
      rhs: `${pow.toFixed(1)}`,
      emphasis: 'invariant',
    },
    {
      id: 'power-form',
      lhs: isOutside ? '(secant–secant)' : '(crossed chords)',
      relation: '=',
      rhs: `|pow| = ${pow.toFixed(1)}`,
      emphasis: 'neutral',
    },
  ];

  return {
    P,
    O,
    r,
    isOutside,
    A,
    B,
    C,
    D,
    productAB,
    productCD,
    readoutRows,
  };
}

/**
 * Build the cyclic quadrilateral model.
 * Four points on the circle — opposite angles sum to 180°.
 */
export function buildCyclicQuadModel(P0: Vec2, P1: Vec2, P2: Vec2, P3: Vec2): CyclicQuadModel {
  const sums = cyclicOppositeSum([P0, P1, P2, P3]);
  const sumAlphaGamma = sums[0];
  const sumBetaDelta = sums[1];

  const readoutRows: ReadoutRow[] = [
    {
      id: 'cyclic-sum-ac',
      lhs: 'α + γ',
      relation: '=',
      rhs: `${sumAlphaGamma.toFixed(1)}°`,
      emphasis: 'invariant',
    },
    {
      id: 'cyclic-sum-bd',
      lhs: 'β + δ',
      relation: '=',
      rhs: `${sumBetaDelta.toFixed(1)}°`,
      emphasis: 'invariant',
    },
  ];

  return {
    pts: [P0, P1, P2, P3],
    sumAlphaGamma,
    sumBetaDelta,
    readoutRows,
  };
}

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Create a DragSpec that constrains a point to lie on the circle.
 */
function circleConstraint(O: Vec2, r: number): { constraint: (p: Vec2) => Vec2 } {
  return {
    constraint: (p: Vec2): Vec2 => projectOntoCircle(p, O, r),
  };
}

/**
 * Point at given angle (degrees) on the circle.
 */
function ptAt(angleDeg: number, O: Vec2, r: number): Vec2 {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: O.x + r * Math.cos(rad), y: O.y + r * Math.sin(rad) };
}

/** Gap (px) between a point and its label, measured radially from the centre. */
const LABEL_GAP = 18;

/**
 * Offset that pushes a point's label radially OUTWARD from the circle centre,
 * so labels (A, B, P, …) sit just outside the circle instead of overlapping it.
 * Degenerate at the centre itself (O) — falls back to a small upward nudge.
 * Pair with `textAnchor: 'middle'` so the glyph centres on the offset position.
 */
function outwardLabelOffset(pt: Vec2, centre: Vec2, gap = LABEL_GAP): Vec2 {
  const dx = pt.x - centre.x;
  const dy = pt.y - centre.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-9) return { x: 0, y: -gap };
  return { x: (dx / len) * gap, y: (dy / len) * gap };
}

// ============================================================================
// State shape
// ============================================================================

interface CircleState {
  mode: CircleMode;
  showCentralAngle: boolean;
  snapDiameter: boolean;
  angleA: number;
  angleB: number;
  angleP: number;
  angleT: number;
  angleQ0: number;
  angleQ1: number;
  angleQ2: number;
  angleQ3: number;
  powerP: Vec2;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): ExperimentSpec<CircleState, GeometryKey, GeometryContext> {
  // Closure-stashed handles created in setup():
  let figure: FigureHandle;
  let marks: MarksHandle;
  let readout: ReadoutHandle;

  // Helper: apply preset locally (used by initialState and actions)
  function applyPresetLocal(state: CircleState, presetId: string, advanced: boolean): CircleState {
    const preset = CIRCLE_PRESETS.find((p) => p.id === presetId);
    if (!preset || (preset.advancedOnly && !advanced)) return state;
    const next: CircleState = { ...state, mode: preset.mode };
    if (preset.angleA !== undefined) next.angleA = preset.angleA;
    if (preset.angleB !== undefined) next.angleB = preset.angleB;
    if (preset.angleP !== undefined) next.angleP = preset.angleP;
    if (preset.powerPointOffset !== undefined) {
      next.powerP = {
        x: CIRCLE_CENTRE.x + preset.powerPointOffset * CIRCLE_RADIUS,
        y: CIRCLE_CENTRE.y,
      };
    }
    return next;
  }

  const ADVANCED_MODES: readonly CircleMode[] = [
    'cyclic-quad',
    'alternate-segment',
    'power-of-point',
  ];

  // The mode used for display + the dropdown value. When advanced is off and the
  // stored mode is advanced-only, it falls back to 'inscribed' (the bespoke mount
  // reset currentMode the same way when advanced was switched off).
  function effectiveModeFor(state: CircleState, advanced: boolean): CircleMode {
    return !advanced && ADVANCED_MODES.includes(state.mode) ? 'inscribed' : state.mode;
  }

  function defaultState(): CircleState {
    return {
      mode: 'inscribed',
      showCentralAngle: true,
      snapDiameter: false,
      angleA: 0,
      angleB: 120,
      angleP: 240,
      angleT: 60,
      angleQ0: 20,
      angleQ1: 100,
      angleQ2: 200,
      angleQ3: 320,
      powerP: { x: CIRCLE_CENTRE.x + 60, y: CIRCLE_CENTRE.y },
    };
  }

  return {
    labelKey: 'experiment.circle-theorems',
    demoId: '2d-geometry',
    advancedKey: 'circle-theorems',

    initialState(): CircleState {
      return defaultState();
    },

    applyPreset(state, presetId, ctx): CircleState {
      return applyPresetLocal(state, presetId, ctx.advanced);
    },

    setup(_ctx, controller) {
      figure = controller.createFigure();
      marks = controller.createMarks();
      readout = controller.createReadout();
    },

    render(state: CircleState, ctx): void {
      // When advanced is off, advanced-only modes fall back to inscribed for
      // display — matching the bespoke mount, whose advanced-off handler reset
      // currentMode to 'inscribed'. We never mutate state here (render is pure);
      // the fallback is a read-only effective mode used for drawing.
      const effectiveMode = effectiveModeFor(state, ctx.advanced);
      switch (effectiveMode) {
        case 'inscribed':
          renderInscribedMode(state);
          break;
        case 'tangent':
          renderTangentMode(state);
          break;
        case 'cyclic-quad':
          renderCyclicQuadMode(state);
          break;
        case 'power-of-point':
          renderPowerMode(state);
          break;
        case 'alternate-segment':
          renderAlternateSegmentMode(state);
          break;
      }

      function renderInscribedMode(state: CircleState): void {
        let A = ptAt(state.angleA, CIRCLE_CENTRE, CIRCLE_RADIUS);
        let B = ptAt(state.angleB, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const P = ptAt(state.angleP, CIRCLE_CENTRE, CIRCLE_RADIUS);

        if (state.snapDiameter) {
          const result = snapToDiameter(A, B, CIRCLE_CENTRE, CIRCLE_RADIUS, SNAP_TOLERANCE_DEG);
          if (result.snapped) {
            A = result.A;
            B = result.B;
          }
        }

        const model = buildInscribedModel(A, B, P, CIRCLE_CENTRE, CIRCLE_RADIUS);

        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'circ-O',
            at: CIRCLE_CENTRE,
            style: { className: 'geo-centre-pt' },
          },
          {
            kind: 'point',
            id: 'circ-rim',
            at: { x: CIRCLE_CENTRE.x + CIRCLE_RADIUS, y: CIRCLE_CENTRE.y },
            // Invisible anchor: only defines the circle's radius (center + through).
            // radius 0 stops a stray dot showing at 3 o'clock once A drags away from it.
            style: { radius: 0 },
          },
          {
            kind: 'circle',
            id: 'circle-main',
            center: 'circ-O',
            through: 'circ-rim',
            style: { className: 'geo-circle' },
          },
          {
            kind: 'point',
            id: 'circ-A',
            at: A,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'circ-B',
            at: B,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'circ-P',
            at: P,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-image-pt' },
          },
          {
            kind: 'segment',
            id: 'chord-AB',
            from: 'circ-A',
            to: 'circ-B',
            // Dashed: AB is the subtending chord; the inscribed angle ∠APB is the
            // focus, so de-emphasise the chord relative to the radii OA/OB.
            style: { className: 'geo-tri-side', dashed: true },
          },
          {
            kind: 'segment',
            id: 'inscribed-PA',
            from: 'circ-P',
            to: 'circ-A',
            style: { className: 'geo-image-poly' },
          },
          {
            kind: 'segment',
            id: 'inscribed-PB',
            from: 'circ-P',
            to: 'circ-B',
            style: { className: 'geo-image-poly' },
          },
          {
            kind: 'label',
            id: 'lbl-A',
            anchor: 'circ-A',
            offset: outwardLabelOffset(A, CIRCLE_CENTRE),
            text: 'A',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-B',
            anchor: 'circ-B',
            offset: outwardLabelOffset(B, CIRCLE_CENTRE),
            text: 'B',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-P',
            anchor: 'circ-P',
            offset: outwardLabelOffset(P, CIRCLE_CENTRE),
            text: 'P',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-O',
            anchor: 'circ-O',
            offset: { x: 12, y: -10 },
            text: 'O',
            textAnchor: 'middle',
          },
        ];

        if (state.showCentralAngle) {
          elems.push(
            {
              kind: 'segment',
              id: 'central-OA',
              from: 'circ-O',
              to: 'circ-A',
              // Continuous radius OA (solid --ink line).
              style: { className: 'geo-ray' },
            },
            {
              kind: 'segment',
              id: 'central-OB',
              from: 'circ-O',
              to: 'circ-B',
              // Continuous radius OB (solid --ink line).
              style: { className: 'geo-ray' },
            },
          );
        }

        figure.update(elems);

        const markSpecs: MarkSpec[] = [
          {
            kind: 'angle',
            id: 'angle-APB',
            vertex: P,
            from: A,
            to: B,
            label: `${model.inscribedAngleDeg.toFixed(0)}°`,
          },
        ];

        if (state.showCentralAngle) {
          markSpecs.push({
            kind: 'angle',
            id: 'angle-AOB',
            vertex: CIRCLE_CENTRE,
            from: A,
            to: B,
            radius: 30,
            // > 180° (∠APB > 90°, P on the minor arc): sweep the reflex arc so the
            // drawn wedge matches the 2·∠APB label and opens away from P.
            reflex: model.centralAngleDeg > 180,
            label: `${model.centralAngleDeg.toFixed(0)}°`,
          });
        }

        if (model.isThales) {
          markSpecs.push({
            kind: 'right-angle',
            id: 'right-angle-P',
            vertex: P,
            from: A,
            to: B,
          });
        }

        marks.update(markSpecs);
        readout.update(model.readoutRows);
      }

      function renderTangentMode(state: CircleState): void {
        const T = ptAt(state.angleT, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const model = buildTangentModel(T, CIRCLE_CENTRE);

        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'circ-O',
            at: CIRCLE_CENTRE,
            style: { className: 'geo-centre-pt' },
          },
          {
            kind: 'point',
            id: 'circ-rim',
            at: { x: CIRCLE_CENTRE.x + CIRCLE_RADIUS, y: CIRCLE_CENTRE.y },
            // Invisible anchor: only defines the circle's radius (center + through).
            // radius 0 stops a stray dot showing at 3 o'clock once A drags away from it.
            style: { radius: 0 },
          },
          {
            kind: 'circle',
            id: 'circle-main',
            center: 'circ-O',
            through: 'circ-rim',
            style: { className: 'geo-circle' },
          },
          {
            kind: 'point',
            id: 'tang-T',
            at: T,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'tang-end-a',
            at: model.tangentEndA,
            style: { className: 'geo-intercept-pt' },
          },
          {
            kind: 'point',
            id: 'tang-end-b',
            at: model.tangentEndB,
            style: { className: 'geo-intercept-pt' },
          },
          {
            kind: 'segment',
            id: 'radius-OT',
            from: 'circ-O',
            to: 'tang-T',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'tangent-line',
            from: 'tang-end-a',
            to: 'tang-end-b',
            style: { className: 'geo-intercept-seg' },
          },
          {
            kind: 'label',
            id: 'lbl-T',
            anchor: 'tang-T',
            offset: outwardLabelOffset(T, CIRCLE_CENTRE),
            text: 'T',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-O',
            anchor: 'circ-O',
            offset: { x: 12, y: -10 },
            text: 'O',
            textAnchor: 'middle',
          },
        ];

        figure.update(elems);

        const markSpecs: MarkSpec[] = [
          {
            kind: 'right-angle',
            id: 'right-angle-T',
            vertex: T,
            from: CIRCLE_CENTRE,
            to: model.tangentEndA,
          },
        ];
        marks.update(markSpecs);

        readout.update([
          {
            id: 'tangent-perp',
            lhs: 'OT ⊥ tangent',
            relation: '=',
            rhs: '90°',
            emphasis: 'invariant',
          },
        ]);
      }

      function renderCyclicQuadMode(state: CircleState): void {
        const P0 = ptAt(state.angleQ0, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const P1 = ptAt(state.angleQ1, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const P2 = ptAt(state.angleQ2, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const P3 = ptAt(state.angleQ3, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const model = buildCyclicQuadModel(P0, P1, P2, P3);

        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'circ-O',
            at: CIRCLE_CENTRE,
            style: { className: 'geo-centre-pt' },
          },
          {
            kind: 'point',
            id: 'circ-rim',
            at: { x: CIRCLE_CENTRE.x + CIRCLE_RADIUS, y: CIRCLE_CENTRE.y },
            // Invisible anchor: only defines the circle's radius (center + through).
            // radius 0 stops a stray dot showing at 3 o'clock once A drags away from it.
            style: { radius: 0 },
          },
          {
            kind: 'circle',
            id: 'circle-main',
            center: 'circ-O',
            through: 'circ-rim',
            style: { className: 'geo-circle' },
          },
          {
            kind: 'point',
            id: 'quad-0',
            at: P0,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'quad-1',
            at: P1,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-image-pt' },
          },
          {
            kind: 'point',
            id: 'quad-2',
            at: P2,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'quad-3',
            at: P3,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-image-pt' },
          },
          {
            kind: 'polygon',
            id: 'cyclic-quad',
            vertices: ['quad-0', 'quad-1', 'quad-2', 'quad-3'],
            style: { className: 'geo-preimage-poly' },
          },
          {
            kind: 'label',
            id: 'lbl-Q0',
            anchor: 'quad-0',
            offset: outwardLabelOffset(P0, CIRCLE_CENTRE),
            text: 'A',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-Q1',
            anchor: 'quad-1',
            offset: outwardLabelOffset(P1, CIRCLE_CENTRE),
            text: 'B',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-Q2',
            anchor: 'quad-2',
            offset: outwardLabelOffset(P2, CIRCLE_CENTRE),
            text: 'C',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-Q3',
            anchor: 'quad-3',
            offset: outwardLabelOffset(P3, CIRCLE_CENTRE),
            text: 'D',
            textAnchor: 'middle',
          },
        ];

        figure.update(elems);

        const markSpecs: MarkSpec[] = [
          { kind: 'angle', id: 'angle-Q0', vertex: P0, from: P3, to: P1, label: 'α' },
          { kind: 'angle', id: 'angle-Q1', vertex: P1, from: P0, to: P2, label: 'β' },
          { kind: 'angle', id: 'angle-Q2', vertex: P2, from: P1, to: P3, label: 'γ' },
          { kind: 'angle', id: 'angle-Q3', vertex: P3, from: P2, to: P0, label: 'δ' },
        ];
        marks.update(markSpecs);
        readout.update(model.readoutRows);
      }

      function renderPowerMode(state: CircleState): void {
        const model = buildPowerModel(state.powerP, CIRCLE_CENTRE, CIRCLE_RADIUS);

        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'circ-O',
            at: CIRCLE_CENTRE,
            style: { className: 'geo-centre-pt' },
          },
          {
            kind: 'point',
            id: 'circ-rim',
            at: { x: CIRCLE_CENTRE.x + CIRCLE_RADIUS, y: CIRCLE_CENTRE.y },
            // Invisible anchor: only defines the circle's radius (center + through).
            // radius 0 stops a stray dot showing at 3 o'clock once A drags away from it.
            style: { radius: 0 },
          },
          {
            kind: 'circle',
            id: 'circle-main',
            center: 'circ-O',
            through: 'circ-rim',
            style: { className: 'geo-circle' },
          },
          {
            kind: 'point',
            id: 'power-P',
            at: state.powerP,
            draggable: {},
            style: { className: 'geo-dilation-centre' },
          },
          { kind: 'point', id: 'pow-A', at: model.A, style: { className: 'geo-preimage-pt' } },
          { kind: 'point', id: 'pow-B', at: model.B, style: { className: 'geo-preimage-pt' } },
          { kind: 'point', id: 'pow-C', at: model.C, style: { className: 'geo-image-pt' } },
          { kind: 'point', id: 'pow-D', at: model.D, style: { className: 'geo-image-pt' } },
          {
            kind: 'segment',
            id: 'pow-chord1',
            from: 'pow-A',
            to: 'pow-B',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'pow-chord2',
            from: 'pow-C',
            to: 'pow-D',
            style: { className: 'geo-intercept-seg' },
          },
          {
            kind: 'label',
            id: 'lbl-P',
            anchor: 'power-P',
            offset: outwardLabelOffset(state.powerP, CIRCLE_CENTRE),
            text: 'P',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-A',
            anchor: 'pow-A',
            offset: outwardLabelOffset(model.A, CIRCLE_CENTRE),
            text: 'A',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-B',
            anchor: 'pow-B',
            offset: outwardLabelOffset(model.B, CIRCLE_CENTRE),
            text: 'B',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-C',
            anchor: 'pow-C',
            offset: outwardLabelOffset(model.C, CIRCLE_CENTRE),
            text: 'C',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-D',
            anchor: 'pow-D',
            offset: outwardLabelOffset(model.D, CIRCLE_CENTRE),
            text: 'D',
            textAnchor: 'middle',
          },
        ];

        figure.update(elems);
        marks.update([]);
        readout.update(model.readoutRows);
      }

      function renderAlternateSegmentMode(state: CircleState): void {
        const T = ptAt(state.angleT, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const tangModel = buildTangentModel(T, CIRCLE_CENTRE);
        const altAngle = state.angleT + 130;
        const Palt = ptAt(altAngle, CIRCLE_CENTRE, CIRCLE_RADIUS);
        const Aalt = ptAt(state.angleT + 60, CIRCLE_CENTRE, CIRCLE_RADIUS);

        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'circ-O',
            at: CIRCLE_CENTRE,
            style: { className: 'geo-centre-pt' },
          },
          {
            kind: 'point',
            id: 'circ-rim',
            at: { x: CIRCLE_CENTRE.x + CIRCLE_RADIUS, y: CIRCLE_CENTRE.y },
            // Invisible anchor: only defines the circle's radius (center + through).
            // radius 0 stops a stray dot showing at 3 o'clock once A drags away from it.
            style: { radius: 0 },
          },
          {
            kind: 'circle',
            id: 'circle-main',
            center: 'circ-O',
            through: 'circ-rim',
            style: { className: 'geo-circle' },
          },
          {
            kind: 'point',
            id: 'tang-T',
            at: T,
            draggable: circleConstraint(CIRCLE_CENTRE, CIRCLE_RADIUS),
            style: { className: 'geo-preimage-pt' },
          },
          { kind: 'point', id: 'alt-A', at: Aalt, style: { className: 'geo-preimage-pt' } },
          { kind: 'point', id: 'alt-P', at: Palt, style: { className: 'geo-image-pt' } },
          {
            kind: 'point',
            id: 'tang-end-a',
            at: tangModel.tangentEndA,
            style: { className: 'geo-intercept-pt' },
          },
          {
            kind: 'point',
            id: 'tang-end-b',
            at: tangModel.tangentEndB,
            style: { className: 'geo-intercept-pt' },
          },
          {
            kind: 'segment',
            id: 'tangent-line',
            from: 'tang-end-a',
            to: 'tang-end-b',
            style: { className: 'geo-intercept-seg' },
          },
          {
            kind: 'segment',
            id: 'chord-TA',
            from: 'tang-T',
            to: 'alt-A',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'inscribed-PA',
            from: 'alt-P',
            to: 'alt-A',
            style: { className: 'geo-image-poly' },
          },
          {
            kind: 'segment',
            id: 'inscribed-PT',
            from: 'alt-P',
            to: 'tang-T',
            style: { className: 'geo-image-poly' },
          },
          {
            kind: 'label',
            id: 'lbl-T',
            anchor: 'tang-T',
            offset: outwardLabelOffset(T, CIRCLE_CENTRE),
            text: 'T',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-A',
            anchor: 'alt-A',
            offset: outwardLabelOffset(Aalt, CIRCLE_CENTRE),
            text: 'A',
            textAnchor: 'middle',
          },
          {
            kind: 'label',
            id: 'lbl-P',
            anchor: 'alt-P',
            offset: outwardLabelOffset(Palt, CIRCLE_CENTRE),
            text: 'P',
            textAnchor: 'middle',
          },
        ];

        figure.update(elems);

        const chordTangentAngle = inscribedAngle(Aalt, tangModel.tangentEndA, T);
        const inscribedAlt = inscribedAngle(T, Aalt, Palt);

        const markSpecs: MarkSpec[] = [
          {
            kind: 'angle',
            id: 'angle-chord-tang',
            vertex: T,
            from: Aalt,
            to: tangModel.tangentEndA,
            label: `${chordTangentAngle.toFixed(0)}°`,
          },
          {
            kind: 'angle',
            id: 'angle-inscribed',
            vertex: Palt,
            from: T,
            to: Aalt,
            label: `${inscribedAlt.toFixed(0)}°`,
          },
        ];
        marks.update(markSpecs);

        readout.update([
          {
            id: 'alt-segment',
            lhs: 'chord–tangent∠',
            relation: '=',
            rhs: `inscribed∠ = ${inscribedAlt.toFixed(1)}°`,
            emphasis: 'invariant',
          },
        ]);
      }
    },

    panel(state: CircleState, ctx): PanelRow<GeometryKey, CircleState>[] {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);

      const modeOptions: { value: string; labelKey: GeometryKey }[] = [
        { value: 'inscribed', labelKey: 'circle.mode.inscribed' },
        { value: 'tangent', labelKey: 'circle.mode.tangent' },
      ];
      if (ctx.advanced) {
        modeOptions.push(
          { value: 'cyclic-quad', labelKey: 'circle.mode.cyclic-quad' },
          { value: 'alternate-segment', labelKey: 'circle.mode.alternate-segment' },
          { value: 'power-of-point', labelKey: 'circle.mode.power-of-point' },
        );
      }

      const rows: PanelRow<GeometryKey, CircleState>[] = [
        {
          kind: 'dropdown',
          id: 'circle-mode',
          labelKey: 'panel.mode',
          options: modeOptions,
          value: effectiveMode,
          onChange: (v) => ({ ...state, mode: v as CircleMode }),
        },
      ];

      if (effectiveMode === 'inscribed') {
        rows.push(
          {
            kind: 'toggle',
            labelKey: 'circle.panel.show-central-angle',
            value: state.showCentralAngle,
            onChange: (v) => ({ ...state, showCentralAngle: v }),
          },
          {
            kind: 'toggle',
            labelKey: 'circle.panel.snap-to-diameter',
            value: state.snapDiameter,
            onChange: (v) => ({ ...state, snapDiameter: v }),
          },
        );
      }

      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (state: CircleState, ctx): ActionBarSpec<GeometryKey, CircleState> => ({
      onReset: () => defaultState(),
      onRandomise: (s) => {
        const next = { ...s };
        if (s.mode === 'inscribed') {
          next.angleA = ctx.prng() * 360;
          next.angleB = ctx.prng() * 360;
          next.angleP = (next.angleA + next.angleB) / 2 + 180 + (ctx.prng() * 60 - 30);
        } else if (s.mode === 'tangent') {
          next.angleT = ctx.prng() * 360;
        } else if (s.mode === 'cyclic-quad') {
          const sorted = [
            ctx.prng() * 360,
            ctx.prng() * 360,
            ctx.prng() * 360,
            ctx.prng() * 360,
          ].sort((a, b) => a - b);
          [next.angleQ0, next.angleQ1, next.angleQ2, next.angleQ3] = sorted as [
            number,
            number,
            number,
            number,
          ];
        } else if (s.mode === 'power-of-point') {
          const offset = (ctx.prng() * 2 - 1) * CIRCLE_RADIUS * 1.5;
          next.powerP = { x: CIRCLE_CENTRE.x + offset, y: CIRCLE_CENTRE.y + offset * 0.3 };
        }
        return next;
      },
      presets: CIRCLE_PRESETS.filter(
        (p) =>
          p.mode === effectiveModeFor(state, ctx.advanced) && (ctx.advanced || !p.advancedOnly),
      ).map((p) => ({ id: p.id, labelKey: p.labelKey })),
      onPreset: (s, id) => applyPresetLocal(s, id, ctx.advanced),
    }),

    onDrag(state: CircleState, pointId: string, at: Vec2): CircleState {
      if (state.mode === 'inscribed') {
        if (pointId === 'circ-A') {
          const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
          const newAngleA =
            Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
          let next: CircleState = { ...state, angleA: newAngleA };
          if (next.snapDiameter) {
            const A = ptAt(next.angleA, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const B = ptAt(next.angleB, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const r = snapToDiameter(A, B, CIRCLE_CENTRE, CIRCLE_RADIUS, SNAP_TOLERANCE_DEG);
            if (r.snapped) {
              next = {
                ...next,
                angleA:
                  Math.atan2(r.A.y - CIRCLE_CENTRE.y, r.A.x - CIRCLE_CENTRE.x) * (180 / Math.PI),
                angleB:
                  Math.atan2(r.B.y - CIRCLE_CENTRE.y, r.B.x - CIRCLE_CENTRE.x) * (180 / Math.PI),
              };
            }
          }
          return next;
        } else if (pointId === 'circ-B') {
          const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
          const newAngleB =
            Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
          let next: CircleState = { ...state, angleB: newAngleB };
          if (next.snapDiameter) {
            const A = ptAt(next.angleA, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const B = ptAt(next.angleB, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const r = snapToDiameter(A, B, CIRCLE_CENTRE, CIRCLE_RADIUS, SNAP_TOLERANCE_DEG);
            if (r.snapped) {
              next = {
                ...next,
                angleA:
                  Math.atan2(r.A.y - CIRCLE_CENTRE.y, r.A.x - CIRCLE_CENTRE.x) * (180 / Math.PI),
                angleB:
                  Math.atan2(r.B.y - CIRCLE_CENTRE.y, r.B.x - CIRCLE_CENTRE.x) * (180 / Math.PI),
              };
            }
          }
          return next;
        } else if (pointId === 'circ-P') {
          const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
          const newAngleP =
            Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
          return { ...state, angleP: newAngleP };
        }
      } else if (state.mode === 'tangent') {
        if (pointId === 'tang-T') {
          const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
          const newAngleT =
            Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
          return { ...state, angleT: newAngleT };
        }
      } else if (state.mode === 'cyclic-quad') {
        const qMap: Record<string, () => CircleState> = {
          'quad-0': () => {
            const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const newAngle =
              Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
            return { ...state, angleQ0: newAngle };
          },
          'quad-1': () => {
            const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const newAngle =
              Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
            return { ...state, angleQ1: newAngle };
          },
          'quad-2': () => {
            const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const newAngle =
              Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
            return { ...state, angleQ2: newAngle };
          },
          'quad-3': () => {
            const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
            const newAngle =
              Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
            return { ...state, angleQ3: newAngle };
          },
        };
        const handler = qMap[pointId];
        if (handler) return handler();
      } else if (state.mode === 'power-of-point') {
        if (pointId === 'power-P') {
          return { ...state, powerP: at };
        }
      } else if (state.mode === 'alternate-segment') {
        if (pointId === 'tang-T') {
          const proj = projectOntoCircle(at, CIRCLE_CENTRE, CIRCLE_RADIUS);
          const newAngleT =
            Math.atan2(proj.y - CIRCLE_CENTRE.y, proj.x - CIRCLE_CENTRE.x) * (180 / Math.PI);
          return { ...state, angleT: newAngleT };
        }
      }
      return state;
    },

    applyShotState(state: CircleState, shotId: string): CircleState {
      if (shotId === 'circle-theorems') {
        // The shot is the thales preset (not advancedOnly), so advanced=true is
        // safe here. If a future shot targets an advancedOnly preset, pass the
        // real advanced flag instead of hardcoding true.
        return applyPresetLocal(state, 'thales', true);
      }
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Re-export for convenience
export { sub, normalize, scale, add, dist };
